import { todayPT } from "@homebound/activesupport";
import { Css, ScrollableParent } from "@homebound/beam";
import { useCallback } from "react";
import { useHistory, useParams } from "react-router";
import { NamedFragment } from "src/fragments/commonFragments.generated";
import { BidContractType, SavePlanBasedBidContractLineItemInput } from "src/generated/graphqlTypes";
import { createBidPackagesDetailUrl } from "src/RouteUrls";
import { fail, pushInNextLoop, queryResult } from "src/utils";
import { PlanBasedBidsTableV2 } from "./components/PlanBasedBidsTableV2";
import {
  EnterPlanBasedBidPage_BidPackageFragment,
  useEnterPlanBasedBidPage_SaveBidContractMutation,
  useEnterPlanBasedBidPage_SavePlanBasedBidContractLineItemsMutation,
  useEnterPlanBasedBidPageQuery,
} from "./EnterPlanBasedBidPage.generated";

export function EnterPlanBasedBidPage() {
  const { bidPackageId, tradePartnerId } = useParams<{ bidPackageId: string; tradePartnerId: string }>();
  const query = useEnterPlanBasedBidPageQuery({ variables: { bidPackageId, tradePartnerId } });
  return queryResult(query, ({ bidPackage, tradePartner }) => (
    <EnterPlanBasedBidPageView bidPackage={bidPackage} tradePartner={tradePartner} />
  ));
}

type EnterPlanBasedBidPageViewProps = {
  bidPackage: EnterPlanBasedBidPage_BidPackageFragment;
  tradePartner: NamedFragment;
};

function EnterPlanBasedBidPageView(props: EnterPlanBasedBidPageViewProps) {
  const { bidPackage, tradePartner } = props;
  const history = useHistory();
  const existingBcr = bidPackage.latestVersion.latestBidContractRevisions.find(
    (b) => b.bidContract.tradePartner?.id === tradePartner.id,
  );
  const bidRequest = bidPackage.activeRequests.find((r) => r.tradePartner.id === tradePartner.id);

  const [saveBidContract] = useEnterPlanBasedBidPage_SaveBidContractMutation();
  const [savePbBcli] = useEnterPlanBasedBidPage_SavePlanBasedBidContractLineItemsMutation();

  const onSave = useCallback(
    async (lineItems: SavePlanBasedBidContractLineItemInput[]) => {
      // If we are creating a new plan-based bid we will first need to create the bid contract;
      // the backend resolver auto-creates the initial BidContractRevision when a BC has none, so
      // saveBidContract is enough to get a BCR id back to attach the PB-BCLIs to.
      let bcrId = existingBcr?.id;
      if (!bcrId) {
        const result = await saveBidContract({
          variables: {
            input: {
              tradePartnerId: tradePartner.id,
              type: BidContractType.UserCreatedGroupBased,
              bidRequestId: bidRequest?.id ?? fail(`Bid request not found for trade partner ${tradePartner.id}`),
              startDate: todayPT(),
            },
          },
        });
        bcrId = result.data?.saveBidContract.bidContract.currentRevision.id;
      }
      if (!bcrId) return;

      await savePbBcli({ variables: { bidContractRevisionId: bcrId, lineItems } });
      pushInNextLoop(history, createBidPackagesDetailUrl(bidPackage.bidPackageGroup!.id, bidPackage.id));
    },
    [existingBcr, savePbBcli, bidPackage, saveBidContract, tradePartner, bidRequest, history],
  );

  return (
    <div css={Css.df.fdc.bgGray100.$}>
      <div css={Css.xl2.tac.p2.fs0.$}>Enter Bid Information</div>
      <ScrollableParent xss={Css.fg1.df.fdc.px2.$}>
        <PlanBasedBidsTableV2 bidPackage={bidPackage} mode="edit" tradePartnerId={tradePartner.id} onSave={onSave} />
      </ScrollableParent>
    </div>
  );
}
